import { Component, Input, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Agent, AgentServicev2 } from '../agent-builder-services/agent.service';
import { LanguageServices } from 'src/app/services/LanguageServices';

/** um objetivo dentro de uma execucao, como fica gravado na alTable */
interface ObjetivoExecutado {
    _id?: string;
    sessionId?: string;
    agentId?: string;
    goal?: string;
    step?: number;
    /** pending, running, completed, error ou paused */
    status?: string;
    lastMessage?: string;
    result?: string;
    error?: string;
    stackTrace?: string;
    createdAt?: number;
    updatedAt?: number;
}

/** um disparo do cron: todos os objetivos que compartilham o mesmo sessionId */
interface Execucao {
    sessionId: string;
    objetivos: ObjetivoExecutado[];
    /** o mais antigo entre os objetivos: quando o disparo comecou */
    iniciadaEm: number;
    /** o mais recente: ate quando houve movimento */
    atualizadaEm: number;
    /** resumo da execucao inteira, derivado dos objetivos */
    situacao: string;
    aberta: boolean;
}

/**
 * Execucoes do agente no modo autonomo.
 *
 * Le a fila (alTable), onde o AutonomousAgentRunner grava um documento por
 * objetivo. Ate a Fase do autonomo, esses documentos nao guardavam nem o
 * resultado nem o sessionId - por isso esta tela so faz sentido com o backend
 * ja corrigido.
 */
@Component({
    selector: 'agent-builder-executions',
    templateUrl: './agent-builder-executions.component.html',
    styleUrls: ['./agent-builder-executions.component.scss'],
    standalone: false
})
export class AgentBuilderExecutionsComponent implements OnInit {

    @Input() agent: Agent;

    execucoes: Execucao[] = [];
    carregando = false;

    /** 'todas' ou um dos status */
    filtroDeSituacao = 'todas';

    // ---- painel de detalhe ----
    detalheAberto = false;
    objetivoSelecionado: ObjetivoExecutado = null;

    constructor(
        private agentService: AgentServicev2,
        private message: NzMessageService,
        private ls: LanguageServices
    ) {}

    ngOnInit(): void {
        this.carregar();
    }

    carregar(): void {

        if (this.agent == null || this.agent._id == null) {
            return;
        }

        // sem alTable configurada nao ha onde ler: o proprio agente ainda nao
        // foi preparado para o modo autonomo
        if (this.agent.alTable == null || this.agent.alTable.length === 0) {
            this.execucoes = [];
            return;
        }

        this.carregando = true;

        this.agentService.getAutonomousRuns(this.agent).subscribe({
            next: (retorno) => {
                this.execucoes = this.agruparPorSessao(this.extrairLista(retorno));
                this.carregando = false;
            },
            error: (erro) => {
                console.error('Erro ao carregar as execucoes:', erro);
                this.execucoes = [];
                this.carregando = false;
                this.message.error(this.ls.instant('agentBuilder.executions.loadError'));
            }
        });
    }

    /** o findnative devolve ora um array direto, ora um objeto com data */
    private extrairLista(retorno: any): ObjetivoExecutado[] {

        if (retorno == null) {
            return [];
        }

        if (Array.isArray(retorno)) {
            return retorno as ObjetivoExecutado[];
        }

        if (Array.isArray(retorno.data)) {
            return retorno.data as ObjetivoExecutado[];
        }

        return [];
    }

    /**
     * Junta os objetivos pelo sessionId: cada sessao e um disparo do cron.
     *
     * Objetivo sem sessionId aparece numa sessao propria em vez de sumir -
     * documento gravado antes da correcao do toDocument nao tem o campo, e
     * esconder isso daria a impressao de que a execucao nunca aconteceu.
     */
    private agruparPorSessao(objetivos: ObjetivoExecutado[]): Execucao[] {

        let porSessao = new Map<string, Execucao>();

        for (let objetivo of objetivos) {

            let chave = objetivo.sessionId;

            if (chave == null || chave.length === 0) {
                chave = 'sem-sessao::' + objetivo._id;
            }

            let execucao = porSessao.get(chave);

            if (execucao == null) {
                execucao = {
                    sessionId: chave,
                    objetivos: [],
                    iniciadaEm: objetivo.createdAt ?? 0,
                    atualizadaEm: objetivo.updatedAt ?? 0,
                    situacao: 'pending',
                    aberta: false
                };
                porSessao.set(chave, execucao);
            }

            execucao.objetivos.push(objetivo);

            if ((objetivo.createdAt ?? 0) < execucao.iniciadaEm || execucao.iniciadaEm === 0) {
                execucao.iniciadaEm = objetivo.createdAt ?? 0;
            }

            if ((objetivo.updatedAt ?? 0) > execucao.atualizadaEm) {
                execucao.atualizadaEm = objetivo.updatedAt ?? 0;
            }
        }

        let resultado: Execucao[] = [];

        porSessao.forEach(execucao => {
            execucao.situacao = this.resumirSituacao(execucao.objetivos);
            resultado.push(execucao);
        });

        // mais recentes primeiro
        resultado.sort((a, b) => b.iniciadaEm - a.iniciadaEm);

        return resultado;
    }

    /**
     * Situacao da execucao inteira, na ordem do que mais importa saber:
     * erro primeiro, depois o que ainda esta em andamento, e so entao concluido.
     */
    private resumirSituacao(objetivos: ObjetivoExecutado[]): string {

        let temErro = false;
        let temRodando = false;
        let temPendente = false;
        let temPausado = false;

        for (let objetivo of objetivos) {

            if (objetivo.status === 'error') {
                temErro = true;
            }

            if (objetivo.status === 'running') {
                temRodando = true;
            }

            if (objetivo.status === 'pending') {
                temPendente = true;
            }

            if (objetivo.status === 'paused') {
                temPausado = true;
            }
        }

        if (temErro) {
            return 'error';
        }

        if (temRodando) {
            return 'running';
        }

        if (temPausado) {
            return 'paused';
        }

        if (temPendente) {
            return 'pending';
        }

        return 'completed';
    }

    /** execucoes que sobrevivem ao filtro da tela */
    get execucoesFiltradas(): Execucao[] {

        if (this.filtroDeSituacao === 'todas') {
            return this.execucoes;
        }

        let resultado: Execucao[] = [];

        for (let execucao of this.execucoes) {
            if (execucao.situacao === this.filtroDeSituacao) {
                resultado.push(execucao);
            }
        }

        return resultado;
    }

    alternarExecucao(execucao: Execucao): void {
        execucao.aberta = !execucao.aberta;
    }

    abrirDetalhe(objetivo: ObjetivoExecutado): void {
        this.objetivoSelecionado = objetivo;
        this.detalheAberto = true;
    }

    fecharDetalhe(): void {
        this.detalheAberto = false;
    }

    /** cor da tag por situacao, no vocabulario do ng-zorro */
    corDaSituacao(situacao: string): string {

        if (situacao === 'error') {
            return 'red';
        }

        if (situacao === 'running') {
            return 'processing';
        }

        if (situacao === 'completed') {
            return 'green';
        }

        if (situacao === 'paused') {
            return 'orange';
        }

        return 'default';
    }

    /** chave de traducao da situacao */
    rotuloDaSituacao(situacao: string): string {

        if (situacao == null || situacao.length === 0) {
            return 'agentBuilder.executions.status.pending';
        }

        return 'agentBuilder.executions.status.' + situacao;
    }

    /**
     * Os campos de data vem como epoch em milissegundos
     * (System.currentTimeMillis no Java), e nao como Date do Mongo.
     */
    dataLegivel(epoch: number): Date {

        if (epoch == null || epoch === 0) {
            return null;
        }

        return new Date(epoch);
    }
}
